import { useState } from "react";
import { Voucher } from "@/lib/accounting/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Send, Phone, Clock, CheckCircle2, AlertCircle, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { renderInvoiceHTML, InvoiceTemplate } from "./InvoiceTemplates";
import jsPDF from "jspdf";

// ── Types ────────────────────────────────────────────────────────────────

interface WhatsAppDelivery {
  id: string;
  invoiceNumber: string;
  partyName: string;
  phoneNumber: string;
  sentAt: string;
  status: "sent" | "delivered" | "read" | "failed";
  amount: number;
}

// ── PDF Generation ───────────────────────────────────────────────────────

function generateInvoicePDFBlob(voucher: Voucher): Blob {
  const doc = new jsPDF();
  const companyName = "ABC Trading Co.";

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(companyName, 20, 25);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("GSTIN: 29ABCDE1234F1Z5", 20, 32);

  // Invoice details
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("TAX INVOICE", 150, 25);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Invoice: ${voucher.invoiceNumber || voucher.voucherNumber}`, 150, 35);
  doc.text(`Date: ${new Date(voucher.date).toLocaleDateString("en-IN")}`, 150, 42);

  // Divider
  doc.setDrawColor(200);
  doc.line(20, 50, 190, 50);

  // Party
  doc.setFont("helvetica", "bold");
  doc.text("Bill To:", 20, 60);
  doc.setFont("helvetica", "normal");
  doc.text(voucher.partyName || "Customer", 20, 67);

  // Table header
  let y = 82;
  doc.setFillColor(240, 240, 240);
  doc.rect(20, y - 5, 170, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.text("Ledger", 22, y);
  doc.text("Debit (₹)", 120, y);
  doc.text("Credit (₹)", 155, y);
  y += 10;

  // Lines
  doc.setFont("helvetica", "normal");
  voucher.lines.forEach((line) => {
    doc.text(line.ledgerName.substring(0, 40), 22, y);
    if (line.debit > 0) doc.text(line.debit.toLocaleString("en-IN"), 120, y);
    if (line.credit > 0) doc.text(line.credit.toLocaleString("en-IN"), 155, y);
    y += 7;
  });

  // GST
  if (voucher.gstAmount && voucher.gstAmount > 0) {
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.text(`GST: ₹${voucher.gstAmount.toLocaleString("en-IN")}`, 120, y);
    y += 7;
  }

  // Total
  y += 3;
  doc.line(20, y - 3, 190, y - 3);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(`Total: ₹${voucher.totalDebit.toLocaleString("en-IN")}`, 120, y + 4);

  // Footer
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("This is a computer generated invoice.", 20, 280);

  return doc.output("blob");
}

// ── Format Phone ─────────────────────────────────────────────────────────

function formatIndianPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("91") && cleaned.length === 12) return cleaned;
  if (cleaned.length === 10) return `91${cleaned}`;
  return cleaned;
}

function isValidIndianPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, "");
  return (cleaned.length === 10 && /^[6-9]/.test(cleaned)) ||
    (cleaned.length === 12 && cleaned.startsWith("91") && /^91[6-9]/.test(cleaned));
}

// ── WhatsApp Message Builder ─────────────────────────────────────────────

function buildWhatsAppMessage(voucher: Voucher, companyName = "ABC Trading Co."): string {
  const invoiceNum = voucher.invoiceNumber || voucher.voucherNumber;
  const amount = `₹${voucher.totalDebit.toLocaleString("en-IN")}`;
  const date = new Date(voucher.date);
  const dueDate = new Date(date);
  dueDate.setDate(dueDate.getDate() + 30);
  const dueDateStr = dueDate.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  return `Hello${voucher.partyName ? ` ${voucher.partyName}` : ""},

Your invoice from *${companyName}* is ready.

📄 *Invoice:* ${invoiceNum}
💰 *Amount:* ${amount}
📅 *Due Date:* ${dueDateStr}
${voucher.gstAmount ? `🏷️ *GST:* ₹${voucher.gstAmount.toLocaleString("en-IN")}` : ""}

Please make the payment at the earliest.

Thank you for your business! 🙏`;
}

// ── Send Dialog Component ────────────────────────────────────────────────

interface WhatsAppSendDialogProps {
  voucher: Voucher;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WhatsAppSendDialog({ voucher, open, onOpenChange }: WhatsAppSendDialogProps) {
  const { toast } = useToast();
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(() => buildWhatsAppMessage(voucher));

  const handleSend = () => {
    if (!isValidIndianPhone(phone)) {
      toast({ title: "Invalid Phone", description: "Enter a valid 10-digit Indian mobile number", variant: "destructive" });
      return;
    }

    setSending(true);
    const formattedPhone = formatIndianPhone(phone);
    const encoded = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encoded}`;

    // Track delivery
    const delivery: WhatsAppDelivery = {
      id: crypto.randomUUID(),
      invoiceNumber: voucher.invoiceNumber || voucher.voucherNumber,
      partyName: voucher.partyName || "Customer",
      phoneNumber: phone,
      sentAt: new Date().toISOString(),
      status: "sent",
      amount: voucher.totalDebit,
    };

    // Store in localStorage for tracking
    const existing = JSON.parse(localStorage.getItem("whatsapp_deliveries") || "[]");
    existing.unshift(delivery);
    localStorage.setItem("whatsapp_deliveries", JSON.stringify(existing.slice(0, 200)));

    window.open(whatsappUrl, "_blank");

    setTimeout(() => {
      setSending(false);
      toast({
        title: "WhatsApp Opened",
        description: `Invoice ${voucher.invoiceNumber || voucher.voucherNumber} message ready to send to ${phone}`,
      });
      onOpenChange(false);
    }, 1000);
  };

  const handleDownloadPDF = () => {
    const blob = generateInvoicePDFBlob(voucher);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${voucher.invoiceNumber || voucher.voucherNumber}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "PDF Downloaded", description: "Attach this PDF to your WhatsApp message" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <MessageCircle className="h-5 w-5 text-success" />
            Send Invoice via WhatsApp
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invoice Summary */}
          <div className="rounded-lg bg-muted/50 border border-border p-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{voucher.invoiceNumber || voucher.voucherNumber}</span>
              <Badge variant="outline" className="text-xs">
                ₹{voucher.totalDebit.toLocaleString("en-IN")}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">{voucher.partyName || "Customer"} • {new Date(voucher.date).toLocaleDateString("en-IN")}</p>
          </div>

          {/* Phone Input */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Customer Phone Number</label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 h-10 bg-muted border border-border rounded-md text-sm text-muted-foreground">
                🇮🇳 +91
              </div>
              <Input
                placeholder="9876543210"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="flex-1"
                maxLength={10}
              />
            </div>
            {phone.length > 0 && !isValidIndianPhone(phone) && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Enter a valid 10-digit mobile number
              </p>
            )}
          </div>

          {/* Message Preview */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Message Preview</label>
            <div className="rounded-lg bg-[hsl(var(--muted))] border border-border p-3 max-h-48 overflow-y-auto">
              <pre className="text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed">{message}</pre>
            </div>
            <p className="text-xs text-muted-foreground">Edit the message before sending if needed</p>
          </div>

          {/* PDF Download */}
          <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-border bg-muted/30">
            <FileText className="h-8 w-8 text-accent shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Invoice PDF</p>
              <p className="text-xs text-muted-foreground">Download PDF to attach in WhatsApp</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="gap-1.5 shrink-0">
              <FileText className="h-3.5 w-3.5" /> Download PDF
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={sending || !isValidIndianPhone(phone)}
            className="gap-1.5 bg-success text-white hover:bg-success/90"
          >
            {sending ? <Clock className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Opening WhatsApp..." : "Send via WhatsApp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delivery Tracker Component ───────────────────────────────────────────

export function WhatsAppDeliveryTracker() {
  const deliveries: WhatsAppDelivery[] = JSON.parse(localStorage.getItem("whatsapp_deliveries") || "[]");

  const statusConfig: Record<string, { icon: typeof CheckCircle2; className: string; label: string }> = {
    sent: { icon: Send, className: "text-primary bg-primary/10", label: "Sent" },
    delivered: { icon: CheckCircle2, className: "text-success bg-success/10", label: "Delivered" },
    read: { icon: CheckCircle2, className: "text-accent bg-accent/10", label: "Read" },
    failed: { icon: AlertCircle, className: "text-destructive bg-destructive/10", label: "Failed" },
  };

  if (deliveries.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageCircle className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
        <p className="text-muted-foreground text-sm">No WhatsApp messages sent yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {deliveries.slice(0, 20).map((d) => {
        const sc = statusConfig[d.status] || statusConfig.sent;
        return (
          <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${sc.className}`}>
              <sc.icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground truncate">{d.invoiceNumber}</span>
                <Badge variant="outline" className="text-xs">₹{d.amount.toLocaleString("en-IN")}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {d.partyName} • {d.phoneNumber} • {new Date(d.sentAt).toLocaleString("en-IN")}
              </p>
            </div>
            <Badge className={`text-xs ${sc.className} border-0`}>{sc.label}</Badge>
          </div>
        );
      })}
    </div>
  );
}

// ── Quick Send Button (for table rows) ───────────────────────────────────

interface WhatsAppQuickButtonProps {
  voucher: Voucher;
  size?: "sm" | "default";
}

export function WhatsAppQuickButton({ voucher, size = "sm" }: WhatsAppQuickButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size={size}
        className={size === "sm" ? "h-7 w-7 p-0" : "gap-1.5"}
        title="Send via WhatsApp"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <MessageCircle className={size === "sm" ? "h-3.5 w-3.5 text-success" : "h-4 w-4 text-success"} />
        {size !== "sm" && "WhatsApp"}
      </Button>
      {open && <WhatsAppSendDialog voucher={voucher} open={open} onOpenChange={setOpen} />}
    </>
  );
}
